import axios from 'axios';
import { utilityService } from './utility-service';

export type UploadConfig = {
    url: string;
    files: FileList | File[];
    params?: UploadConfigParams[];
}

export type UploadConfigParams = {
    name: string;
    value: string;
}

export type DownloadConfig = {
    url: string;
}

export type FindConfig = {
    url: string;
}

export type FileItem = {
    id: string;
    fileName: string;
    fileSize: string;
    creationDate: Date;
    creationUser: string;
}

const downloadLink = (downloadUrl: string, fileName?: string) => {
    const fakeLink = document.createElement('a');
    fakeLink.setAttribute('href', downloadUrl);
    fakeLink.setAttribute('download', fileName || '');
    document.body.appendChild(fakeLink);
    fakeLink.click();
    fakeLink.remove();
}

export const upload = async (config: UploadConfig, onUploadProgress?: (progressEvent: any) => void): Promise<void> => {
    // input validation
    if (!config) {
        throw new Error('config argument missing');
    }
    if (!Object.prototype.hasOwnProperty.call(config, 'url')) {
        throw new Error('url property is missing in config argument');
    }
    if (!Object.prototype.hasOwnProperty.call(config, 'files')) {
        throw new Error('files property is missing in config argument');
    }
    config.params = config.params || [];
    // check browser feature
    if (navigator.appVersion.indexOf('MSIE 8.') > -1 || navigator.appVersion.indexOf('MSIE 9.') > -1) {
        throw new Error(`Feature not supported in your current browser: ${navigator.appVersion}, be sure to run on Internet Explorer >= 10 or on any other ever green browser`);
    }

    const formData = new FormData();

    if (config.files !== undefined && config.files !== null) {
        for (let i = 0; i < config.files.length; i += 1) {
            formData.append(`file${i}`, config.files[i]);
        }
    }

    for (const param of config.params) {
        formData.append(param.name, param.value);
    }

    return await axios.post(config.url, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        },
        onUploadProgress
    });

}

export const download = (config: DownloadConfig): void => {
    // input validation
    if (!config) {
        throw new Error('config argument missing');
    }
    if (!Object.prototype.hasOwnProperty.call(config, 'url')) {
        throw new Error('url property is missing in config argument');
    }
    const downloadUrl = axios.defaults.baseURL
        ? `${axios.defaults.baseURL}${config.url}`
        : config.url;
    downloadLink(downloadUrl);
}

export const downloadBlob = (fileName: string, mediaType: string, fileContent: string): void => {
    const blob = utilityService.b64toBlob(fileContent, mediaType, 512);

    if (navigator.appVersion.toString().indexOf('.NET') > 0) {
        window.navigator.msSaveBlob(blob, fileName);
    } else {
        const downloadUrl = URL.createObjectURL(blob);
        downloadLink(downloadUrl, fileName);        
    }
}

export const getFiles = async (config: FindConfig): Promise<FileItem[]> => {
    // input validation
    if (!config) {
        throw new Error('config argument missing');
    }
    if (!Object.prototype.hasOwnProperty.call(config, 'url')) {
        throw new Error('url property is missing in config argument');
    }

    return await axios.get(config.url);
}

export const deleteFile = async (config: FindConfig): Promise<void> => {
    // input validation
    if (!config) {
        throw new Error('config argument missing');
    }
    if (!Object.prototype.hasOwnProperty.call(config, 'url')) {
        throw new Error('url property is missing in config argument');
    }

    return await axios.delete(config.url);
}

export const getLink = (config: DownloadConfig): string => {
    // input validation
    if (!config) {
        throw new Error('config argument missing');
    }
    if (!Object.prototype.hasOwnProperty.call(config, 'url')) {
        throw new Error('url property is missing in config argument');
    }

    return config.url;
}